import React, { useEffect, useState } from "react";
import { Modal, Form, DatePicker, Radio, Input, Button, message } from "antd";
import axios from "axios";
import moment from "moment";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LeaveRequestForm = ({ visible, onClose, auditorId, fetchData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [leaveMode, setLeaveMode] = useState("FULL_DAY");

  const [leaveBalance, setLeaveBalance] = useState({
    sick: 0,
    casual: 0,
    compensation: 0,
  });

  const apiUrl = process.env.REACT_APP_API_URL;

  /* ---------------- Fetch Leave Balance ---------------- */
  useEffect(() => {
    if (!auditorId) return;

    const fetchLeaveBalance = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/api/worklogs/calculateLeaveData/${auditorId}`
        );
        // console.log(res, "leave detail");
        setLeaveBalance({
          sick: res.data.nonLOPLeavesAvailable.sick.overall,
          casual: res.data.nonLOPLeavesAvailable.casual.overall,
          compensation: res.data.compensation.available,
        });
      } catch {
        message.error("Failed to fetch leave balance");
      }
    };

    fetchLeaveBalance();
  }, [auditorId]);

  const noPaidLeaveLeft =
    leaveBalance.sick <= 0 && leaveBalance.casual <= 0;

  const noPaidCompLeaveLeft =
    leaveBalance.compensation <= 0;

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const payload = {
        userId: auditorId,
        leaveMode,
        reason: values.reason,
      };

      /* ---- Permission ---- */
      if (leaveMode === "PERMISSION") {
        payload.permissionHours = values.permissionHours;
        payload.fromDate = values.permissionDate;
        payload.toDate = values.permissionDate;
      }

      /* ---- Half / Full Day ---- */
      else {
        payload.leaveType = values.leaveType;

        if (leaveMode === "HALF_DAY") {
          payload.fromDate = values.date;
          payload.toDate = values.date;
        } else {
          payload.fromDate = values.dates[0];
          payload.toDate = values.dates[1];
        }
      }
      // console.log(payload, " auditor pov request leave modal post data");
      await axios.post(
        `${apiUrl}/api/worklogs/submitLeaveRequest`,
        payload
      );

      message.success("Leave request submitted");
      form.resetFields();
      fetchData?.();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Request Leave"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        {/* Leave Mode */}
        <Form.Item label="Leave Mode">
          <Radio.Group
            value={leaveMode}
            onChange={(e) => {
              setLeaveMode(e.target.value);
              form.resetFields();
            }}
          >
            <Radio.Button value="FULL_DAY">Full Day</Radio.Button>
            <Radio.Button value="HALF_DAY">Half Day</Radio.Button>
            <Radio.Button value="PERMISSION">Permission</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* FULL DAY */}
        {leaveMode === "FULL_DAY" && (
          <Form.Item
            name="dates"
            label="Leave Duration"
            rules={[{ required: true }]}
          >
            <RangePicker
              disabledDate={(d) => d && d < moment().startOf("day")}
            />
          </Form.Item>
        )}

        {/* HALF DAY */}
        {leaveMode === "HALF_DAY" && (
          <Form.Item
            name="date"
            label="Leave Date"
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(d) => d && d < moment().startOf("day")}
            />
          </Form.Item>
        )}

        {/* PERMISSION DATE */}
        {leaveMode === "PERMISSION" && (
          <Form.Item
            name="permissionDate"
            label="Permission Date"
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(d) => d && d < moment().startOf("day")}
            />
          </Form.Item>
        )}

        {/* LEAVE TYPE */}
        {leaveMode !== "PERMISSION" && (
          <Form.Item
            name="leaveType"
            label="Leave Type"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              {!noPaidLeaveLeft && (
                <>
                  <Radio
                    value="sickLeave"
                    disabled={leaveBalance.sick <= 0}
                  >
                    Sick Leave ({leaveBalance.sick})
                  </Radio>
                  <Radio
                    value="casualLeave"
                    disabled={leaveBalance.casual <= 0}
                  >
                    Casual Leave ({leaveBalance.casual})
                  </Radio>
                </>
              )}
              {!noPaidCompLeaveLeft && leaveMode === "FULL_DAY" && (
                <>
                  <Radio
                    value="compensationLeave"
                    disabled={leaveBalance.compensation <= 0}
                  >
                    Compensation Leave ({leaveBalance.compensation})
                  </Radio>
                </>
              )}
              {noPaidLeaveLeft && (
                <Radio value="lop">Loss of Pay (LOP)</Radio>
              )}
            </Radio.Group>
          </Form.Item>
        )}

        {/* PERMISSION HOURS */}
        {leaveMode === "PERMISSION" && (
          <Form.Item
            name="permissionHours"
            label="Permission Hours"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={1}>1 Hour</Radio>
              <Radio value={2}>2 Hours</Radio>
              <Radio value={3}>3 Hours</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {/* Reason */}
        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={loading} block>
          Apply Leave
        </Button>
      </Form>
    </Modal>
  );
};

export default LeaveRequestForm;
